"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Section } from "@/components/ui/Section";
import {
  Monitor,
  Smartphone,
  Code2,
  Headset,
  AppWindow,
  LucideIcon,
} from "lucide-react";

// Map keys to icons
const iconMap = {
  teamspeak: Headset,
  websites: AppWindow,
  desktop: Monitor,
  mobile: Smartphone,
  dev: Code2,
};

export function Services() {
  const t = useTranslations("Index");
  const tServices = useTranslations("Services.data");
  const keys = ["teamspeak", "websites", "desktop", "mobile", "dev"] as const;

  return (
    <Section
      id="services"
      title={t("services_title")}
      subtitle={t("services_subtitle")}
      className="relative bg-(--bg-primary) overflow-hidden py-24"
      contentClassName="relative z-10"
      /*separator={{
        position: "top",
        variant: "layered-wave",
        fillClassName: "fill-[var(--bg-primary)]",
      }}*/
    >
      {/* Ambient Background Glows - Top Left & Bottom Right */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-125 h-125 bg-(--brand-primary)/5 blur-[120px] rounded-full pointer-events-none z-0 mix-blend-screen" />
      <div className="absolute bottom-1/4 translate-y-1/2 -right-1 w-125 h-125 bg-(--brand-primary)/5 blur-[120px] rounded-full pointer-events-none z-0 mix-blend-screen" />

      <div className="w-full mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 justify-center">
          {keys.map((key, index) => {
            const Icon = iconMap[key];
            return (
              <ServiceCard
                key={key}
                index={index}
                icon={Icon}
                title={tServices(`${key}.title`)}
                description={tServices(`${key}.description`)}
              />
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function ServiceCard({
  index,
  icon: Icon,
  title,
  description,
}: {
  index: number;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  const number = (index + 1).toString().padStart(2, "0");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-5%" }}
      transition={{
        delay: index * 0.05,
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group relative flex flex-col text-center p-6 rounded-lg bg-(--bg-card) border border-(--border-subtle) transition-colors duration-500 hover:border-(--text-primary)/10 overflow-hidden"
    >
      {/* Background Number */}
      <div className="absolute top-2 right-4 text-6xl font-bold font-outfit text-(--text-primary)/5 select-none pointer-events-none group-hover:text-(--text-primary)/10 transition-colors duration-500">
        {number}
      </div>

      {/* Header: Icon + Title + Divider */}
      <div className="relative z-10 flex flex-col items-center gap-4 mb-4">
        <div className="relative flex items-center justify-center w-12 h-12 rounded-md bg-(--text-primary)/5 text-(--text-secondary) group-hover:text-(--text-primary) transition-colors duration-300">
          <Icon className="w-8 h-8" />
        </div>
        <h3 className="text-base font-medium font-outfit text-(--text-primary) transition-colors duration-300">
          {title}
        </h3>
        <div className="h-1 w-8 bg-(--border-subtle) opacity-50 mb-4 group-hover:bg-(--brand-primary) rounded-full transition-colors duration-500 ease-out" />
      </div>

      {/* Description */}
      <p className="relative z-10 text-sm leading-relaxed text-(--text-secondary) opacity-80 group-hover:opacity-100 transition-opacity duration-300">
        {description}
      </p>
    </motion.div>
  );
}
